export const STAFF_ROLE_TYPES: { value: string; label: string }[] = [
  { value: 'PRINCIPAL', label: 'Principal' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'TEACHER', label: 'Teacher' },
  { value: 'BUS_DRIVER', label: 'Bus Driver' },
  { value: 'SECURITY_GUARD', label: 'Security Guard' },
  { value: 'ESCORT', label: 'Escort' },
  { value: 'COORDINATOR', label: 'Coordinator' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'CAB_DRIVER', label: 'Cab Driver' },
];

/** Shown on Staff Permissions page only — excludes Admin (school admin has full access by default). */
export const STAFF_PERMISSIONS_ROLE_TYPES: { value: string; label: string }[] = STAFF_ROLE_TYPES.filter(
  (r) => r.value !== 'ADMIN'
);

/**
 * Top-down authority: Level 1 = highest (school leadership), Level 5 = operational base.
 * Listed in order from top of org → bottom.
 */
export const AUTHORITY_LEVELS: { value: string; label: string }[] = [
  { value: 'LEVEL_1', label: 'Level 1 — Executive / School leadership (top)' },
  { value: 'LEVEL_2', label: 'Level 2 — Senior management' },
  { value: 'LEVEL_3', label: 'Level 3 — Mid-level / Lead within scope' },
  { value: 'LEVEL_4', label: 'Level 4 — Supervised / Team' },
  { value: 'LEVEL_5', label: 'Level 5 — Operational (base)' },
];

export type AuthorityLevelRow = { value: string; label?: string; order?: number };

/** Ladder shown in UI: custom school.settings.authorityLevels or app defaults (top → down). */
export function resolveAuthorityLevelsFromSchool(
  custom?: AuthorityLevelRow[] | null | undefined
): { value: string; label: string }[] {
  if (Array.isArray(custom) && custom.length > 0) {
    return [...custom]
      .filter((x) => x && String(x.value || '').trim())
      .map((x, i) => ({
        value: String(x.value).trim(),
        label: String(x.label || x.value || '').trim() || String(x.value).trim(),
        order: Number.isFinite(Number(x.order)) ? Number(x.order) : i,
      }))
      .sort((a, b) => a.order - b.order || a.value.localeCompare(b.value))
      .map(({ value, label }) => ({ value, label }));
  }
  return AUTHORITY_LEVELS.map((l) => ({ value: l.value, label: l.label }));
}

export function getAuthorityLevelLabel(
  code: string | undefined,
  resolvedLevels?: { value: string; label: string }[]
): string {
  if (!code) return '—';
  const list = resolvedLevels ?? AUTHORITY_LEVELS;
  return list.find((l) => l.value === code)?.label ?? code;
}

/** Feature matrix ids/paths; drawer and /staff routing derive staff-scoped ids via `utils/staffFeatureAccessPolicy`. */
export const STAFF_FEATURES: { id: string; label: string; path: string; viewOnly: boolean }[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/', viewOnly: true },
  { id: 'dispersals', label: 'Dispersals', path: '/dispersals', viewOnly: false },
  { id: 'students', label: 'Students', path: '/students', viewOnly: false },
  { id: 'studentsList', label: 'Students — List', path: '/students', viewOnly: false },
  { id: 'studentsRelations', label: 'Students — Relations', path: '/students/relations', viewOnly: false },
  { id: 'studentsDispersal', label: 'Students — Dispersal method', path: '/students/dispersal', viewOnly: false },
  { id: 'notifications', label: 'Notifications', path: '/notifications', viewOnly: false },
  { id: 'meetings', label: 'Meetings', path: '/meetings', viewOnly: false },
  { id: 'staff', label: 'Staff', path: '/staff', viewOnly: false },
  { id: 'staffList', label: 'Staff — List', path: '/staff', viewOnly: false },
  { id: 'staffSubjects', label: 'Staff — Subjects', path: '/staff/subjects', viewOnly: false },
  { id: 'staffRoles', label: 'Staff — Staff roles', path: '/staff/roles', viewOnly: false },
  { id: 'classes', label: 'Classes', path: '/classes', viewOnly: false },
  { id: 'notices', label: 'Notices', path: '/notices', viewOnly: false },
  { id: 'school', label: 'School', path: '/school', viewOnly: false },
  { id: 'gates', label: 'Gates', path: '/gates', viewOnly: false },
  { id: 'settings', label: 'Settings', path: '/settings', viewOnly: false },
  { id: 'staffRolePermissions', label: 'Staff Permissions', path: '/staff-role-permissions', viewOnly: true },
  { id: 'payments', label: 'Payments', path: '/payments', viewOnly: false },
];

/**
 * Subset of STAFF_FEATURES used in the Settings page for staff feature access.
 * This only includes top-level features (not sub-tabs like studentsList, staffSubjects, etc.).
 */
export const SETTINGS_STAFF_FEATURES = STAFF_FEATURES.filter((f) =>
  ['dashboard', 'dispersals', 'students', 'notifications', 'meetings', 'staff', 'classes', 'notices', 'school', 'gates', 'settings', 'payments'].includes(f.id)
);

export const STAFF_ROLES: string[] = ['TEACHER', 'MANAGER', 'COORDINATOR', 'BUS_DRIVER', 'SECURITY_GUARD', 'ESCORT'];

export const PICKUP_OPTIONS: { value: string; label: string }[] = [
  { value: 'PARENT_PICKUP', label: 'Parent pickup' },
  { value: 'SELF', label: 'Self' },
  { value: 'SCHOOL_BUS', label: 'School bus' },
  { value: 'PERSONAL_CAB', label: 'Personal cab' },
];
